import { createSlice } from "@reduxjs/toolkit";
import {
  fetchProgramAssignment,
  createProgramAssignment,
  fetchProgramAssignmentDetail,
} from "../../actions/programs/programAssignment";
import {
  fetchQuestionAssociationPrograms,
  fetchQuestionAssociationTournaments,
} from "redux/actions/programs/programsData";
import { listClinics } from "../../actions/users/usersData";

const initialState = {
  programAssignment: {},
  programAssignmentLoading: false,
  programAssignmentCreating: false,
  programAssignmentCreateStep: 1,
  programAssignmentDetail: {},
  programAssignmentDetailLoading: false,
  programAssignmentUpdate: {},
  programAssignmentUpdateLoading: false,
  clinicLoading: false,
  clinicsList: {},
  questionAssociationProgramme: [],
  questionAssociationProgrammeLoading: false,
  questionAssociationTournament: [],
  questionAssociationTournamentLoading: false,
  selectedRoles: [],
  selectedTypes: [],
  selectedOrganizations: [],
  selectedUsers: [],
  selectedUserGroups: [],
  selectedClinics: [],
  all_role_types: false,
  mandatory: true,
  sequential: true,
  assignFormStartDate: null,
  assignFormEndDate: null,
  selected_timezone: "UTC",
  programListPage: 1,

  // filter states
  programNameSearch: "",
  programFilters: [],
  orgFilters: [],
  clinicFilters: [],
  roleFilters: [],
  userTypeFilters: [],
  userGroupFilters: [],
  userFilters: [],
  programAssignmentStatus: "All",
};

export const programAssignmentSlice = createSlice({
  name: "programAssignment",
  initialState,
  reducers: {
    resetProgramAssignment: (state) => {
      state.programAssignment = {};
      state.programAssignmentLoading = false;
      state.programAssignmentCreating = false;
      state.programAssignmentCreateStep = 1;
      state.programAssignmentDetail = {};
      state.programAssignmentDetailLoading = false;
      state.programAssignmentUpdate = {};
      state.programAssignmentUpdateLoading = false;
      state.clinicLoading = false;
      state.clinicsList = {};
      state.questionAssociationProgramme = [];
      state.questionAssociationProgrammeLoading = false;
      state.questionAssociationTournament = [];
      state.questionAssociationTournamentLoading = false;
      state.selectedRoles = [];
      state.selectedTypes = [];
      state.selectedOrganizations = [];
      state.selectedUsers = [];
      state.selectedUserGroups = [];
      state.selectedClinics = [];
      state.all_role_types = false;
      state.mandatory = true;
      state.sequential = true;
      state.assignFormStartDate = new Date().toISOString();
      state.assignFormEndDate = null;
      state.selected_timezone = "UTC";
    },
    setAssignmentState: (state, { payload }) => {
      // payload is an object where key is the state and data is the value
      // set value if state exists in initialState
      Object.keys(payload).forEach((key) => {
        if (key in initialState) {
          state[key] = payload[key];
        }
      });
    },
    toggleRoles: (state, action) => {
      // if payload exists in selectedRoles, remove it else add it
      if (state.selectedRoles.includes(action.payload)) {
        state.selectedRoles = state.selectedRoles.filter(
          (role) => role !== action.payload
        );
      } else {
        state.selectedRoles.push(action.payload);
      }
    },
    toggleTypes: (state, action) => {
      // if payload exists in selectedTypes, remove it else add it
      if (state.selectedTypes.includes(action.payload)) {
        state.selectedTypes = state.selectedTypes.filter(
          (type) => type !== action.payload
        );
      } else {
        state.selectedTypes.push(action.payload);
      }
    },
    toggleOrganizations: (state, action) => {
      // if payload exists in selectedOrganizations, remove it else add it
      if (
        state.selectedOrganizations.find(
          (organization) => organization.org_id === action.payload.org_id
        )
      ) {
        state.selectedOrganizations = state.selectedOrganizations.filter(
          (organization) => organization.org_id !== action.payload.org_id
        );
      } else {
        state.selectedOrganizations.push(action.payload);
      }
    },
    toggleUsers: (state, action) => {
      // if payload exists in selectedUsers, remove it else add it
      if (
        state.selectedUsers.find(
          (user) => user.user_id === action.payload.user_id
        )
      ) {
        state.selectedUsers = state.selectedUsers.filter(
          (user) => user.user_id !== action.payload.user_id
        );
      } else {
        state.selectedUsers.push(action.payload);
      }
    },
    toggleUserGroups: (state, action) => {
      // if payload exists in selectedUserGroups, remove it else add it
      if (
        state.selectedUserGroups.find(
          (userGroup) => userGroup.group_id === action.payload.group_id
        )
      ) {
        state.selectedUserGroups = state.selectedUserGroups.filter(
          (userGroup) => userGroup.group_id !== action.payload.group_id
        );
      } else {
        state.selectedUserGroups.push(action.payload);
      }
    },
    toggleClinics: (state, action) => {
      // if payload exists in selectedClinics, remove it else add it
      if (
        state.selectedClinics.find((clinic) => clinic.id === action.payload.id)
      ) {
        state.selectedClinics = state.selectedClinics.filter(
          (clinic) => clinic.id !== action.payload.id
        );
      } else {
        state.selectedClinics.push(action.payload);
      }
    },
    setQuestionAssociationProgramme: (state, action) => {
      state.questionAssociationProgramme = action.payload;
    },
    setQuestionAssociationTournament: (state, action) => {
      state.questionAssociationTournament = action.payload;
    },
  },
  extraReducers: {
    // get program assignment
    [fetchProgramAssignment.pending]: (state) => {
      state.programAssignmentLoading = true;
    },
    [fetchProgramAssignment.fulfilled]: (state, action) => {
      state.programAssignment = action.payload;
      state.programAssignmentLoading = false;
    },
    [fetchProgramAssignment.rejected]: (state) => {
      state.programAssignmentLoading = false;
    },
    // create program assignment
    [createProgramAssignment.pending]: (state) => {
      state.programAssignmentCreating = true;
    },
    [createProgramAssignment.fulfilled]: (state, action) => {
      state.programAssignmentCreating = false;
    },
    [createProgramAssignment.rejected]: (state) => {
      state.programAssignmentCreating = false;
    },
    // program assignment detail
    [fetchProgramAssignmentDetail.pending]: (state) => {
      state.programAssignmentDetailLoading = true;
    },
    [fetchProgramAssignmentDetail.fulfilled]: (state, action) => {
      state.programAssignmentDetail = action.payload;
      state.programAssignmentDetailLoading = false;
    },
    [fetchProgramAssignmentDetail.rejected]: (state) => {
      state.programAssignmentDetailLoading = false;
    },
    [listClinics.pending]: (state) => {
      state.clinicLoading = true;
    },
    [listClinics.fulfilled]: (state, action) => {
      state.clinicLoading = false;
      state.clinicsList = action.payload;
    },
    [listClinics.rejected]: (state) => {
      state.clinicLoading = false;
    },
    // question association apis
    [fetchQuestionAssociationPrograms.pending]: (state) => {
      state.questionAssociationProgrammeLoading = true;
    },
    [fetchQuestionAssociationPrograms.fulfilled]: (state, action) => {
      state.questionAssociationProgramme = action.payload.results;
      state.questionAssociationProgrammeLoading = false;
    },
    [fetchQuestionAssociationPrograms.rejected]: (state) => {
      state.questionAssociationProgrammeLoading = false;
      state.questionAssociationProgramme = [];
    },
    [fetchQuestionAssociationTournaments.pending]: (state) => {
      state.questionAssociationTournamentLoading = true;
    },
    [fetchQuestionAssociationTournaments.fulfilled]: (state, action) => {
      state.questionAssociationTournament = action.payload.results;
      state.questionAssociationTournamentLoading = false;
    },
    [fetchQuestionAssociationTournaments.rejected]: (state) => {
      state.questionAssociationTournamentLoading = false;
      state.questionAssociationTournament = [];
    },
  },
});

export const {
  toggleRoles,
  toggleTypes,
  toggleOrganizations,
  toggleUsers,
  toggleUserGroups,
  toggleClinics,
  setAssignmentState,
  resetProgramAssignment,
  setQuestionAssociationProgramme,
  setQuestionAssociationTournament,
} = programAssignmentSlice.actions;

export default programAssignmentSlice.reducer;
